import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Globe, ShoppingCart, TrendUp, Sparkle, Lightning, Palette } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const ModernHero = () => {
  const { t } = useTranslation();
  const heroRef = useRef(null);
  const blobRef = useRef(null);
  const floatingElementsRef = useRef(null);
  const serviceCardsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate the main blob
      gsap.fromTo(blobRef.current, 
        { 
          scale: 0.8, 
          rotation: -10,
          borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%'
        },
        { 
          scale: 1, 
          rotation: 5,
          borderRadius: '40% 60% 70% 30% / 50% 60% 30% 70%',
          duration: 3,
          ease: "power2.out",
          repeat: -1,
          yoyo: true
        }
      );

      // Animate floating elements
      gsap.to(floatingElementsRef.current.children, {
        y: -20,
        duration: 2,
        ease: "power2.inOut",
        repeat: -1,
        yoyo: true,
        stagger: 0.3
      });

      // Animate service cards on scroll
      gsap.fromTo(serviceCardsRef.current.children, 
        { 
          y: 50, 
          opacity: 0,
          scale: 0.9
        },
        { 
          y: 0, 
          opacity: 1,
          scale: 1,
          duration: 0.8,
          delay: 1,
          ease: "back.out(1.7)",
          stagger: 0.2
        }
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  const services = [
    {
      icon: <Globe weight="duotone" className="w-8 h-8 text-white" />,
      title: t('modernHero.services.websites.title'),
      description: t('modernHero.services.websites.description'),
      href: '/webdesign',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: <ShoppingCart weight="duotone" className="w-8 h-8 text-white" />,
      title: t('modernHero.services.webshops.title'),
      description: t('modernHero.services.webshops.description'),
      href: '/webshops',
      gradient: 'from-blue-400 to-blue-500'
    },
    {
      icon: <TrendUp weight="duotone" className="w-8 h-8 text-white" />,
      title: t('modernHero.services.optimization.title'),
      description: t('modernHero.services.optimization.description'),
      href: '/seo',
      gradient: 'from-blue-600 to-blue-700'
    }
  ];

  return (
    <section ref={heroRef} className="relative bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div 
          ref={blobRef}
          className="absolute top-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-400/20 opacity-60"
        />
        
        {/* Floating Elements */}
        <div ref={floatingElementsRef} className="absolute inset-0">
          <div className="absolute top-32 left-20 w-16 h-16 bg-gradient-to-br from-yellow-400/30 to-orange-400/30 rounded-full" />
          <div className="absolute top-40 right-32 w-12 h-12 bg-gradient-to-br from-green-400/30 to-emerald-400/30 rounded-full" />
          <div className="absolute bottom-40 left-32 w-20 h-20 bg-gradient-to-br from-pink-400/30 to-red-400/30 rounded-full" />
          <div className="absolute bottom-32 right-20 w-14 h-14 bg-gradient-to-br from-purple-400/30 to-indigo-400/30 rounded-full" />
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.03)_1px,transparent_1px)] bg-[length:50px_50px]" />
      </div>

      <div className="relative z-10 container mx-auto px-4 pt-16 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            
            {/* Left Content */}
            <div className="space-y-8 lg:pr-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-blue-200/50 shadow-sm">
                  <Sparkle weight="duotone" className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">{t('modernHero.badge')}</span>
                </div>
                
                <h1 className="text-5xl md:text-7xl font-bold text-slate-900 leading-tight">
                  {t('modernHero.title.part1')}{' '}
                  <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
                    {t('modernHero.title.part2')}
                  </span>{' '}
                  {t('modernHero.title.part3')}
                </h1>
                
                <p className="text-xl text-slate-600 leading-relaxed max-w-2xl">
                  {t('modernHero.description')}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link
                  to="/start-your-project"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <Lightning weight="duotone" className="w-6 h-6" />
                  {t('modernHero.cta.primary')}
                  <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                
                <Link
                  to="/portfolio"
                  className="group inline-flex items-center justify-center gap-3 bg-white/90 backdrop-blur-sm text-slate-700 px-8 py-4 rounded-full font-semibold text-lg border border-slate-200 hover:border-slate-300 transition-all duration-300 hover:bg-white"
                >
                  <Palette weight="duotone" className="w-6 h-6" />
                  {t('modernHero.cta.secondary')}
                </Link>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="grid grid-cols-3 gap-8 pt-8"
              >
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900">50+</div>
                  <div className="text-sm text-slate-600">{t('modernHero.stats.projects')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900">99.9%</div>
                  <div className="text-sm text-slate-600">{t('modernHero.stats.uptime')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900">24/7</div>
                  <div className="text-sm text-slate-600">{t('modernHero.stats.support')}</div>
                </div>
              </motion.div>
            </div>

            {/* Right Content - Service Cards */}
            <div className="relative lg:pl-8 hidden lg:block">
              <div ref={serviceCardsRef} className="space-y-4">
                {services.map((service, index) => (
                  <motion.div
                    key={service.title}
                    whileHover={{ scale: 1.02, x: 10 }}
                    className="group relative"
                  >
                    <Link
                      to={service.href}
                      className="block bg-white/95 backdrop-blur-sm rounded-xl p-5 shadow-md border border-white/60 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2.5 rounded-lg bg-gradient-to-br ${service.gradient} bg-opacity-15 flex-shrink-0`}>
                          {service.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-slate-900 mb-1 truncate">
                            {service.title}
                          </h3>
                          <p className="text-slate-600 text-sm leading-tight">
                            {service.description}
                          </p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0" />
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModernHero;
